import React from 'react';
import {
    DropdownSelect,
    SingleValue,
    components,
    type OptionProps,
    type SingleValueProps,
    type GroupBase,
} from 'src/components/DropdownWrapper/DropdownWrapper';
import classNames from 'classnames';
import FLAGS from './assets/flags.png';
import { CountryDropdownWrapper, OptionWrapper } from './CountryDropdown.style';

const MAPPED_COUNTRIES = {
    // Map non-standard country codes to CSS classes that exist
    mf: 'fr', // Saint Martin (French part) -> France flag
    bl: 'fr', // Saint Barthélemy -> France flag
    bq: 'nl', // Bonaire -> Netherlands flag
} as const;

interface CountryOptionType {
    id?: string;
    label: string;
    value: string;
}

interface CountryDropdownPropsType {
    className?: string;
    onChange?: (value: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    value?: string;
    options: CountryOptionType[];
}

interface CountryDropdownStateType {
    selected: string;
    value: string;
    focus?: boolean;
}

const getCountryCode = (id: string | undefined): string => {
    if (id === undefined || id === '') return '';

    const lowercaseId = id.toLowerCase();
    const isMappedCountry = (key: string): key is keyof typeof MAPPED_COUNTRIES => key in MAPPED_COUNTRIES;

    return isMappedCountry(lowercaseId) ? MAPPED_COUNTRIES[lowercaseId] : lowercaseId;
};

const CountryFlag = ({ id, label }: { id: string | undefined; label: string }): JSX.Element => {
    const countryCode = getCountryCode(id);

    return (
        <span>
            <span
                className={`famfamfam-flags ${countryCode}`}
                style={{ backgroundImage: `url(${FLAGS})` }}
            />
            {label}
        </span>
    );
};

const CustomOption = (props: OptionProps<CountryOptionType, false, GroupBase<CountryOptionType>>): JSX.Element => {
    const { data, innerRef, innerProps, isFocused, isSelected } = props;

    return (
        <OptionWrapper
            ref={innerRef}
            {...innerProps}
            isFocused={isFocused}
            isSelected={isSelected}
            role='option'
            aria-selected={isSelected}
        >
            <CountryFlag
                id={data.id}
                label={data.label}
            />
        </OptionWrapper>
    );
};

const CustomSingleValue = (
    props: SingleValueProps<CountryOptionType, false, GroupBase<CountryOptionType>>
): JSX.Element => {
    return (
        <components.SingleValue {...props}>
            <CountryFlag
                id={props.data.id}
                label={props.data.label}
            />
        </components.SingleValue>
    );
};

/**
 * @deprecated
 * Please use "CountryDropdownNew" instead of this component
 * src/components/CountryDropdown/CountryDropdownNew.tsx
 */
export class CountryDropdown extends React.PureComponent<CountryDropdownPropsType, CountryDropdownStateType> {
    public constructor(props: CountryDropdownPropsType) {
        super(props);
        this.state = this.createState(props);
    }

    public componentDidUpdate(prevProps: CountryDropdownPropsType): void {
        if (prevProps.value !== this.props.value) {
            this.setState(this.createState(this.props));
        }
    }

    public onChange = (selectedOption: SingleValue<CountryOptionType>): void => {
        if (selectedOption !== null) {
            const { value, label } = selectedOption;
            this.setState({
                value,
                selected: label,
            });

            if (this.props.onChange !== undefined) {
                this.props.onChange(value);
            }
        }
    };

    public createState(props: CountryDropdownPropsType): CountryDropdownStateType {
        const { options, value = '' } = props;
        const selectedByValue = options.find((item) => item.value === value);
        const selected = selectedByValue === undefined ? options.find((item) => item.label === value) : selectedByValue;

        return {
            //@ts-expect-error
            selected: selected === undefined ? (value === '' ? '' : value.split(',')[0]) : selected.label,
            value: value,
        };
    }

    public onFocus = (): void => {
        this.setState({
            focus: true,
        });

        if (this.props.onFocus !== undefined) {
            this.props.onFocus();
        }
    };

    public onBlur = (): void => {
        this.setState({
            focus: false,
        });

        if (this.props.onBlur !== undefined) {
            this.props.onBlur();
        }
    };

    public render(): JSX.Element {
        const { selected, value } = this.state;
        const { options } = this.props;

        const selectedByValue = options.find((opt) => opt.value === value);
        const selectedOption =
            selectedByValue === undefined ? options.find((opt) => opt.label === selected) ?? null : selectedByValue;

        const className = classNames([
            this.props.className,
            'country-dropdown input',
            { focus: this.state.focus },
            { 'has-value': selectedOption !== null },
        ]);

        return (
            <CountryDropdownWrapper className={className}>
                <DropdownSelect
                    options={options}
                    value={selectedOption}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                    components={{
                        Option: CustomOption,
                        SingleValue: CustomSingleValue,
                    }}
                    getOptionValue={(option: CountryOptionType) => option.value}
                    getOptionLabel={(option: CountryOptionType) => option.label}
                    hasInternalLabel={true}
                />
            </CountryDropdownWrapper>
        );
    }
}
